/**
 * Commission engine — additive, does not alter payroll/payment logic.
 *
 * Each rental assigned to a staff member yields exactly ONE commission record in
 * the `staff_commissions` collection. The reconcile function is idempotent, so
 * calling it on create / edit / status-change / delete always leaves the data in
 * the correct state (create, transfer, or remove) with no duplicates.
 *
 * A commission's "paid" status is derived in real time from the employee's
 * commission-type payments (oldest-first), so partial payments just work and
 * nothing needs re-writing when a payment is recorded.
 */

export const DEFAULT_COMMISSION = 25;

/**
 * Ensure the commission record for a rental matches its current state.
 * @param {object}   rental       the rental (must have id; reads staffId/status/...).
 * @param {boolean}  deleted      true when the rental is being removed.
 * @param {object[]} staffList    all staff (to read commissionPerRental).
 * @param {object[]} commissions  all existing commission records.
 * @param {object}   ops          { add, update, remove } from useCollection('staff_commissions').
 */
export async function reconcileRentalCommission({ rental, deleted = false, staffList = [], commissions = [], ops }) {
  if (!rental || !rental.id || !ops) return;
  const { add, update, remove } = ops;
  const rentalId = rental.id;
  const existing = commissions.filter((c) => c.rentalId === rentalId);

  const staffId = rental.staffId || '';
  const staff = staffList.find((s) => s.id === staffId);
  // Commissions only accrue for employees on the "commission" compensation mode.
  const isCommissionStaff = !!staff && staff.salaryType === 'commission';
  const shouldExist = !deleted && rental.status !== 'cancelled' && !!staffId && isCommissionStaff;

  // No commission should exist → remove any (cancelled / deleted / unassigned /
  // employee not on the commission plan).
  if (!shouldExist) {
    for (const c of existing) { try { await remove(c.id); } catch (_) {} }
    return;
  }

  const rate = Number(staff?.commissionPerRental);
  const amount = Number.isFinite(rate) ? rate : DEFAULT_COMMISSION;

  const contractNumber = rental.contractNumber || rental.contractNo || String(rentalId).slice(0, 8).toUpperCase();
  const customer = rental.clientName || '';
  const vehicle = rental.carName || '';
  const date = rental.startDate || new Date().toISOString().split('T')[0];

  if (existing.length === 0) {
    await add({
      staffId, rentalId, contractNumber, customer, vehicle,
      amount, status: 'pending', notes: '', date,
      createdAt: new Date().toISOString(),
    });
    return;
  }

  // Dedupe: keep the first, drop the rest.
  const [keep, ...extra] = existing;
  for (const c of extra) { try { await remove(c.id); } catch (_) {} }

  if (keep.staffId !== staffId) {
    // Transfer to the newly assigned employee (adopt their rate).
    await update(keep.id, { staffId, amount, contractNumber, customer, vehicle });
  } else {
    // Refresh denormalised fields if the rental changed.
    const patch = {};
    if (keep.contractNumber !== contractNumber) patch.contractNumber = contractNumber;
    if (keep.customer !== customer) patch.customer = customer;
    if (keep.vehicle !== vehicle) patch.vehicle = vehicle;
    if (Object.keys(patch).length) await update(keep.id, patch);
  }
}

/** Total commission paid to an employee (commission-type payments only). */
export function totalCommissionPaid(payments = []) {
  return payments
    .filter((p) => p.type === 'commission')
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
}

/** Aggregate commission stats for one employee. */
export function computeCommissionStats(commissions = [], payments = []) {
  const totalEarned = commissions.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const totalPaid = totalCommissionPaid(payments);
  const remaining = Math.max(0, totalEarned - totalPaid);
  const count = commissions.length;
  const avg = count ? totalEarned / count : 0;
  const commPayments = payments.filter((p) => p.type === 'commission');
  const lastPaymentDate = commPayments
    .map((p) => p.paymentDate || p.date || '')
    .sort()
    .slice(-1)[0] || '';
  return { totalEarned, totalPaid, remaining, count, avg, lastPaymentDate };
}

/** Attach a derived paid/pending status to each commission (oldest paid first). */
export function withPaidStatus(commissions = [], totalPaid = 0) {
  const sorted = [...commissions].sort(
    (a, b) => (a.date || '').localeCompare(b.date || '') || (a.createdAt || '').localeCompare(b.createdAt || '')
  );
  let cum = 0;
  return sorted.map((c) => {
    cum += Number(c.amount) || 0;
    return { ...c, status: cum <= totalPaid ? 'paid' : 'pending' };
  });
}
